import { motion, AnimatePresence } from 'framer-motion';
import './ThresholdLockedModal.css';

interface ThresholdLockedModalProps {
    isOpen: boolean;
    onClose: () => void;
    puzzleLabel: string;
    mode: string;
    requiredThreshold: number;
    currentCompletion: number;
}

export function ThresholdLockedModal({
    isOpen,
    onClose,
    puzzleLabel,
    mode,
    requiredThreshold,
    currentCompletion
}: ThresholdLockedModalProps) {
    if (!isOpen) return null;

    const progressPercent = Math.min(100, (currentCompletion / requiredThreshold) * 100);
    const modeName = mode.charAt(0).toUpperCase() + mode.slice(1);
    const remaining = Math.max(0, requiredThreshold - currentCompletion);

    return (
        <AnimatePresence>
            <motion.div
                className="threshold-modal-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
            >
                <motion.div
                    className="threshold-modal-content"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="threshold-modal-header">
                        <span className="threshold-lock-icon">🔒</span>
                        <h2>Puzzle Locked</h2>
                    </div>

                    <div className="threshold-modal-body">
                        <p className="threshold-puzzle-name">{puzzleLabel}</p>
                        <p className="threshold-requirement">
                            Complete <strong>{requiredThreshold}%</strong> of {modeName} puzzles to unlock this challenge!
                        </p>

                        <div className="threshold-progress-container">
                            <div className="threshold-progress-bar">
                                <motion.div
                                    className="threshold-progress-fill"
                                    initial={{ width: 0 }}
                                    animate={{ width: `${progressPercent}%` }}
                                    transition={{ duration: 0.5, ease: "easeOut" }}
                                />
                            </div>
                            <div className="threshold-progress-labels">
                                <span className="current-pct">{Math.round(currentCompletion)}%</span>
                                <span className="target-pct">{requiredThreshold}%</span>
                            </div>
                        </div>

                        <p className="threshold-remaining">
                            {remaining > 0 ? (
                                <>You need <strong>{remaining.toFixed(1)}%</strong> more to unlock!</>
                            ) : (
                                <>Almost there! Keep playing!</>
                            )}
                        </p>
                    </div>

                    <div className="threshold-modal-footer">
                        <button
                            className="threshold-close-btn"
                            onClick={onClose}
                        >
                            Got it!
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
